import * as React             from "react";
import RotateSolidIcon        from "./assets/rotate-solid.svg";
import RulerSolidIcon         from "./assets/ruler-solid.svg";
import UploadSolidIcon        from "./assets/upload-solid.svg";
import ArrowIcon              from "./assets/arrow-solid.svg";
import {Dropdown}             from "@Common/ui-components/dropdown/dropdown";
import {makeChain}            from "@Common/react/cst2";
import {Types}                from "@Common/types";
import {LoadingSpinner}       from "../components/loading-spinner";
import {SelectionTile}        from "../components/selection-tile";
import {TemplateUploadButton} from "../components/template-upload-button";
import {consumeContext}       from "@Xpresso/loaders/consume-context";

import "./idml-upload-layout.scss";



export const DownloadsDropdown = ({ apis, allGuides, layouts }: { apis: any, allGuides: number[], layouts: any }) => {
    const productIds = allGuides.filter(guideId =>
        layouts.some(layout => layout.idProduct == guideId)
    );
    const productNames = layouts
        .filter(layout => allGuides.some(guideId => layout.idProduct == guideId))
        .map(layout => layout.product?.name);

    return Dropdown({
        renderToggle: ({domRef, showExpand, showingExpand}) => (
            <button
                className={"download-guides-button"}
                xd-qa-type="link"
                xd-qa-name={"Download Guides"}
                ref={domRef}
                onClick={() => showExpand(!showingExpand)}
            >
                Download Guides
                {<img src={ArrowIcon} className="arrow-icon"></img>}
            </button>
        ),

        renderExpand: ({close, domRef, position}) => (
            <div
                className="dropdown"
                style={position}
                ref={domRef}
            >
                <div
                    key={"Download All"}
                    className="dropdown-item"
                    xd-qa-type="dropdown_item"
                    xd-qa-name={"download-all"}
                    onClick={() => {
                        apis.next.guideFiles.getGuideFiles([])
                        .then(blob => {
                            const link = document.createElement('a');
                            link.href = window.URL.createObjectURL(blob);
                            link.download = 'All Product Guides.zip';
                            link.click();
                        })
                        .catch(error => console.error('Failed to download all guides:', error));
                    }}
                >
                    All Products <div className="productCount">{allGuides.length}</div>
                </div>
                {productIds.length > 0 && <><div
                    key={"Download Listed Guides"}
                    className="dropdown-item"
                    xd-qa-type="dropdown_item"
                    xd-qa-name={"download-listed"}
                    onClick={() => {
                        apis.next.guideFiles.getGuideFiles(productIds)
                            .then(blob => {
                                const link = document.createElement('a');
                                link.href = window.URL.createObjectURL(blob);
                                link.download = 'Similar Product Guides.zip';
                                link.click();
                            })
                            .catch(error => console.error('Failed to download listed guides:', error));
                    }}
                >
                    Similar Products <div className="productCount">{productIds.length}</div>
                </div>
                <div className="dropdown-divider"></div></>
                }
                {productIds.map((productId, index)=> (
                    <div
                        key={productId}
                        className="dropdown-item"
                        xd-qa-type="dropdown_item"
                        xd-qa-name={productId}
                        onClick={() => {
                            apis.next.guideFiles.getGuideFiles([productId])
                                .then(blob => {
                                    const link = document.createElement('a');
                                    link.href = window.URL.createObjectURL(blob);
                                    link.download = `${productNames[index]}.zip`;
                                    link.click();
                                })
                                .catch(error => console.error(`Failed to download guide for product ID ${productId}:`, error));
                        }}
                    >
                        {productNames[index]}
                    </div>
                ))}
            </div>
        )
    })
};

function firstReadyLayouts(layoutsArray) {
    return layoutsArray.find(layouts => layoutsReady(layouts));
}

function layoutsReady(layouts) {
    return !layouts.loading && layouts.value?.length > 0;
}

function layoutsEmpty(layouts) {
    return !layouts.loading && layouts.value?.length == 0;
}

const renderLayouts = (layouts, onSelectLayout, idmlInfo) => {
    console.log(idmlInfo?.pageCount);
    if (layouts.loading) {
        return <LoadingSpinner text="Loading Layouts" />;
    }
    return layouts.value.map(layout => (
        <SelectionTile
            key={layout.id}
            text={layout.product.name + " - " + layout.name.split(" - ").slice(0, 2).join(" - ")}
            image={layout.images[Object.keys(layout.images)?.[0]]?.url}
            onClick={() => onSelectLayout(layout.id, idmlInfo)}
        />
    ));
};

export const IdmlUploadLayout = ({
    idmlInfo,
    layouts,
    suggestedLayouts,
    rotatedLayouts,
    availableGuides,
    onSelectLayout,
    onUploadComplete,
    onUploadError
}:{
    idmlInfo          : any,
    layouts           : Types.Common.React.Load2.Load2Next<any[]>,
    suggestedLayouts  : Types.Common.React.Load2.Load2Next<any[]>,
    rotatedLayouts    : Types.Common.React.Load2.Load2Next<any[]>,
    availableGuides   : any,
    onSelectLayout    : (v:number, idmlInfo:any) => void,
    onUploadComplete  : (idmlInfo:any) => void,
    onUploadError     : (error:string) => void,
}) => (

    makeChain<JSX.Element>()
    .then(...consumeContext("apis"))

    // render view
    .then(({apis}) => (
        <div className="idml-upload-layout-65s">
            <div className="status">
                {/*loading layouts*/}
                {(layouts.loading || suggestedLayouts.loading || rotatedLayouts.loading) ? (
                    <div className="loading-layouts">
                        <LoadingSpinner text="Loading Layouts" />
                    </div>
                ) : (
                <>
                    {/*too many pages*/}
                    {(idmlInfo?.pageCount > 2) && (
                        <div className="suggestion">
                            <div className="icon-container">
                                <img src={RotateSolidIcon} className="error-icon" />
                            </div>
                            <div className="text-container">
                                <div className="title">Page Count Mismatch</div>
                                <div className="description">
                                    Your file doesn't match our standard products.
                                </div>
                            </div>
                        </div>
                    )}
                    {/*matches found*/}
                    {(idmlInfo?.pageCount <= 2 && layoutsReady(layouts)) && (<>
                        <h2 xd-qa-type="label" xd-qa-name="New Template Upload Status">
                            Successfully uploaded <i>{idmlInfo.fileName}</i> with size of {idmlInfo.pageSize.width}” × {idmlInfo.pageSize.height}”.
                        </h2>
                        <h2>
                            Choose a layout to use with this template or{" "}
                            <a href="#new-template"
                                xd-qa-type="link" xd-qa-name="New Template Upload Again"
                                onClick={(e) => {e.preventDefault(); window.location.hash = "#new-template"}}
                            >return to upload another</a>.
                        </h2></>
                    )}
                    {/*no matches found*/}
                    {(idmlInfo?.pageCount <= 2 && layoutsEmpty(layouts) && layoutsEmpty(suggestedLayouts) && layoutsEmpty(rotatedLayouts)) && (
                        <div className="suggestion">
                            <div className="icon-container">
                                <img src={UploadSolidIcon} className="error-icon" />
                            </div>
                            <div className="text-container">
                                <div className="title">Upload Mismatch</div>
                                <div className="description">
                                    Your file doesn't match our standard products.
                                </div>
                            </div>
                        </div>
                    )}
                    {/*close matches found*/}
                    {(idmlInfo?.pageCount <= 2 && layoutsEmpty(layouts) && layoutsReady(suggestedLayouts)) && (
                        <div className="suggestion">
                            <div className="icon-container">
                                <img src={RulerSolidIcon} className="error-icon" />
                            </div>
                            <div className="text-container">
                                <div className="title">Size Mismatch</div>
                                <div className="description">
                                    Your file doesn't match our standard products, but it's close to the following:
                                </div>
                            </div>
                        </div>
                    )}
                    {/*rotated matches found*/}
                    {(idmlInfo?.pageCount <= 2 && layoutsEmpty(layouts) && layoutsEmpty(suggestedLayouts) && layoutsReady(rotatedLayouts)) && (
                        <div className="suggestion">
                            <div className="icon-container">
                                <img src={RotateSolidIcon} className="error-icon" />
                            </div>
                            <div className="text-container">
                                <div className="title">Orientation Mismatch</div>
                                <div className="description">
                                    Your file doesn't match our standard products, but it's close to the following:
                                </div>
                            </div>
                        </div>
                    )}
                </>)}
            </div>
            {(!layouts.loading && !suggestedLayouts.loading && !rotatedLayouts.loading) && (
                <>
                {/*display layouts*/}
                {(idmlInfo?.pageCount <= 2 && (layoutsReady(layouts))) && (
                    <div className="render-layouts-wrapper">
                        <div className="render-layouts">{renderLayouts(firstReadyLayouts([layouts, suggestedLayouts, rotatedLayouts]), onSelectLayout, idmlInfo)}</div>
                    </div>
                )}
                {(idmlInfo?.pageCount <= 2 && layoutsEmpty(layouts) && (layoutsReady(suggestedLayouts) || layoutsReady(rotatedLayouts))) && (
                    <div className="render-layouts">{renderLayouts(firstReadyLayouts([layouts, suggestedLayouts, rotatedLayouts]), ()=>{}, idmlInfo)}</div>
                )}
                {/*downloads footer*/}
                {(idmlInfo?.pageCount <= 2 && layoutsEmpty(layouts) && (layoutsReady(suggestedLayouts) || layoutsReady(rotatedLayouts))) && (
                    <div className="downloads-footer">
                        <div className="downloads-info">
                            <p>Our design guides can help you make adjustments.</p>
                        </div>
                        <div className="downloads-actions">
                            <DownloadsDropdown
                                apis={apis}
                                allGuides={availableGuides.value}
                                layouts={firstReadyLayouts([layouts, suggestedLayouts, rotatedLayouts])?.value}
                            />
                            <TemplateUploadButton
                                onUploadComplete={onUploadComplete}
                                onUploadError={onUploadError}
                            />
                        </div>
                    </div>
                )}
                {/*downloads body*/}
                {(idmlInfo?.pageCount > 2 || (layoutsEmpty(layouts) && layoutsEmpty(suggestedLayouts) && layoutsEmpty(rotatedLayouts))) && (
                    <div className="downloads-body">
                        <div className="text-container">
                            Our design guides can help you make adjustments.
                        </div>
                        <div className="actions-container">
                            <DownloadsDropdown
                                apis={apis}
                                allGuides={availableGuides.value}
                                layouts={[]}
                            />
                            <TemplateUploadButton
                                onUploadComplete={onUploadComplete}
                                onUploadError={onUploadError}
                            />
                        </div>
                    </div>
                )}
                </>
            )}
        </div>
    ))

    .run()

);